import { useEffect, useMemo } from 'react';

import { matchPath, useHistory, useLocation, useParams } from 'react-router';
import { generatePath } from 'react-router-dom';

import { TeamMemberTypeRoleEnum } from '@/graphql';
import useUser from '@/hooks/useUser';
import { MatchParams } from '@/pages/WorkspaceTeams/types';
import {
  teamChainsPagePath,
  teamModelApisPagePath,
  teamModelsPagePath,
  teamSettingsPagePath,
  teamTrainingPagePath,
} from '@/routes/constants';
import { useRecoilState } from 'recoil';

import { useTeamQuery, useTeamsQuery } from './__generated__/queries.generated';
import { selectedTeamIdState } from './atoms';
import { getUserRoleTitle } from './utils';

const POLL_INTERVAL = 15000;

function useTeam(id: string, poll = false) {
  const { data, loading } = useTeamQuery({
    variables: { id },
    fetchPolicy: 'cache-first',
    skip: !id,
    pollInterval: poll ? POLL_INTERVAL : null,
  });
  const user = useUser();

  const members =
    data?.team?.members.filter(
      (member) => member.user.id === user.id || member.role !== TeamMemberTypeRoleEnum.Admin,
    ) || [];

  return {
    team: data?.team,
    loading,
    members,
    userRoleTitle: getUserRoleTitle(data?.team?.currentUser?.role),
    userRole: data?.team?.currentUser?.role,
  };
}

function useTeams(skip = false) {
  const { data, loading } = useTeamsQuery({
    fetchPolicy: 'cache-first',
    skip,
  });

  return { teams: data?.teams || [], loading };
}

function useSelectedTeamId() {
  const [selectedTeamId, setSelectedTeamId] = useRecoilState(selectedTeamIdState);
  const { teamId: teamIdFromUrl } = useParams<MatchParams>();
  const location = useLocation();
  const history = useHistory();
  const { teams, loading } = useTeams();

  const defaultTeam = useMemo(() => {
    return teams.find((team) => team.default);
  }, [teams]);

  useEffect(() => {
    if (!teamIdFromUrl && !loading && !selectedTeamId) {
      setSelectedTeamId(defaultTeam.id);
    }
  }, [teamIdFromUrl, defaultTeam, loading, selectedTeamId, setSelectedTeamId]);

  useEffect(() => {
    if (teamIdFromUrl) {
      setSelectedTeamId(teamIdFromUrl);
    }
  }, [teamIdFromUrl, setSelectedTeamId]);

  function setTeamId(teamId: string) {
    if (teamIdFromUrl) {
      // this means we are on a page that has :teamId in the url
      // so we need to update the url to the new teamId
      // the internal state will be updated via the useEffect above
      const teamPaths = [
        teamModelsPagePath,
        teamChainsPagePath,
        teamTrainingPagePath,
        teamModelApisPagePath,
        teamSettingsPagePath,
      ];

      // find which team path matches the current location
      const matchedPath = teamPaths.find((path) =>
        matchPath(location.pathname, { path, exact: true }),
      );

      if (matchedPath) {
        const newPath = generatePath(matchedPath, { teamId });
        history.push(newPath);
      }
    } else {
      // otherwise, we are on a page that does not have :teamId in the url
      // so we just need to update the internal state
      setSelectedTeamId(teamId);
    }
  }

  function setDefaultTeamId() {
    const defaultTeam = teams.find((team) => team.default);
    setTeamId(defaultTeam.id);
  }

  return { selectedTeamId, setTeamId, setDefaultTeamId, defaultTeam, loading };
}

export { useTeam, useTeams, useSelectedTeamId };
